<script setup lang="ts">
import { onMounted, ref, Ref } from 'vue';
import {
  Contexts,
  CurrentCaseServiceInterface,
  getContainer,
  getContext,
  Helpers,
  NamedBindings,
  ApplicationEvents,
  Version,
} from '@dandelion/core';
import { isEqual } from 'lodash';

interface Antibiotico {
  codice: number;
  nome: string;
}

const props = defineProps<{
  context: string;
  fullName: string;
  id: string;
}>();

const currentCaseService = getContainer(Contexts.DEFAULT).get(
  NamedBindings.CurrentCaseService
) as CurrentCaseServiceInterface;

const selectedAntibiotics = ref<Array<Antibiotico>>([]); // Array di antibiotici selezionati con nome e codice
const antibiotics = ref<Antibiotico[]>([]);
const filteredAntibiotics = ref<Antibiotico[]>([]);
const searchQuery = ref('');
const dropdownVisible = ref(false);

async function useCurrentContext(context = Contexts.DEFAULT) {
  let crfVersions: Readonly<Ref<ReadonlyMap<string, Version>>> = ref(new Map());
  try {
    crfVersions = ref(currentCaseService.getCrfVersions());
  } catch (e) {}

  const model = await currentCaseService.getModel();

  const codiceForReRender = ref(0);

  getContext(context).addEventListener(
    ApplicationEvents.CaseOpenedForEditing,
    async () => {
      const currentCrfVersions = currentCaseService.getCrfVersions();
      if (!isEqual(currentCrfVersions, crfVersions)) {
        crfVersions = currentCrfVersions;
      }
      codiceForReRender.value = codiceForReRender.value + 1;
    }
  );

  return { codiceForReRender, crfVersions, model };
}

onMounted(async () => {
  try {
    const response = await fetch('/jsonData/antifungini.json');
    const data = await response.json();
    const allAntibiotics: Antibiotico[] = Object.values(
      data
    ).flat() as Antibiotico[];
    antibiotics.value = allAntibiotics;
    filteredAntibiotics.value = allAntibiotics;

    const model = (await useCurrentContext(props.context)).model;
    const selectedAntibioticCodes = Helpers.getObjByPath(props.fullName, model);

    if (selectedAntibioticCodes && selectedAntibioticCodes.length > 0) {
      selectedAntibiotics.value = selectedAntibioticCodes
        .map((code: string) => {
          return antibiotics.value.find(
            (antibiotic) => antibiotic.codice.toString() === code
          );
        })
        .filter(Boolean) as Antibiotico[];

      if (selectedAntibiotics.value.length > 0) {
        searchQuery.value = selectedAntibiotics.value[0].nome;
      }
    }
  } catch (err: any) {
    console.log(err.message);
  }
});

const filterAntibiotics = () => {
  const query = searchQuery.value.toLowerCase();
  filteredAntibiotics.value = antibiotics.value.filter((antibiotic) =>
    antibiotic.nome.toLowerCase().includes(query)
  );
  dropdownVisible.value = true;
};

const selectAntibiotic = async (antibiotic: Antibiotico) => {
  if (!selectedAntibiotics.value.some((a) => a.codice === antibiotic.codice)) {
    selectedAntibiotics.value.push(antibiotic); // Aggiungi l'antibiotico alla lista
  }
  searchQuery.value = ''; // Resetta l'input
  dropdownVisible.value = false; // Nascondi il dropdown

  const model = (await useCurrentContext(props.context)).model;
  Helpers.setObjByPath(
    props.fullName,
    model,
    selectedAntibiotics.value.map((a) => a.codice.toString())
  );
};

const removeAntibiotic = async (antibiotic: Antibiotico) => {
  // Rimuovi l'antibiotico dalla lista selezionata
  selectedAntibiotics.value = selectedAntibiotics.value.filter(
    (a) => a.codice !== antibiotic.codice
  );

  // Aggiorna il modello
  const model = (await useCurrentContext(props.context)).model;
  Helpers.setObjByPath(
    props.fullName,
    model,
    selectedAntibiotics.value.map((a) => a.codice.toString())
  );
};

const handleBlur = () => {
  setTimeout(() => {
    dropdownVisible.value = false;
  }, 200);
};
</script>

<template>
  <div class="autocomplete-container">
    <div class="dropdown-container">
      <!-- Campo di input per il filtro -->
      <input
        type="text"
        v-model="searchQuery"
        placeholder="Cerca un Antifungino..."
        @input="filterAntibiotics"
        @focus="dropdownVisible = true"
        @blur="handleBlur"
        class="autocomplete-input"
      />

      <!-- Dropdown con le opzioni -->
      <ul v-if="dropdownVisible" class="dropdown-list">
        <li
          v-for="antibiotic in filteredAntibiotics"
          :key="antibiotic.codice"
          @click="selectAntibiotic(antibiotic)"
          class="dropdown-item"
        >
          {{ antibiotic.nome }}
        </li>
        <li v-if="filteredAntibiotics.length === 0" class="dropdown-no-results">
          Nessun risultato trovato
        </li>
      </ul>
    </div>
    <!-- Lista degli antibiotici selezionati -->
    <div class="selected-antibiotics">
      <ul>
        <li
          v-for="antibiotic in selectedAntibiotics"
          :key="antibiotic.codice"
          class="selected-antibiotic-item"
        >
          <button @click="removeAntibiotic(antibiotic)" class="remove-button">
            x
          </button>
          <span class="antibiotic-name">{{ antibiotic.nome }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style>
/* Stili invariati, con etichette adattate per gli antibiotici */
.autocomplete-container {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 20px;
  width: 100%;
}

.autocomplete-input {
  width: 100%;
  padding: 8px;
  margin-top: 10px;
  margin-bottom: 0;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.dropdown-list {
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  max-height: 200px;
  max-width: 100%;
  overflow-y: auto;
  z-index: 1000;
  list-style: none;
  padding: 0;
  margin: 0;
}

.dropdown-item {
  padding: 8px;
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: #f0f0f0;
}

.dropdown-no-results {
  padding: 8px;
  color: #999;
}

.selected-antibiotics {
  margin-top: 10px;
}

.selected-antibiotic-item {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.remove-button {
  background: #f44336;
  color: white;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
}

.remove-button:hover {
  background: #d32f2f;
}

.antibiotic-name {
  font-weight: bold;
  color: #333;
}
</style>
